import { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import './Table.scss';

interface Props<T> {
  columns: Column<T>[];
  data: T[];
  className?: string;
  renderCustomRow?: (
    r: T,
    columns: Column<T>[],
    gridTemplateColumns: string,
    i: number
  ) => JSX.Element;
}

export interface Column<T> {
  field: keyof T;
  label: string;
  sortable?: boolean;
  formatter?: (v: T) => string | number | null | JSX.Element;
  minWidth?: number;
  maxWidth?: string;
}

export const Table = <T,>({
  columns,
  data,
  className = '',
  renderCustomRow,
  ...rest
}: Props<T & { id?: string | number }> & HTMLAttributes<HTMLDivElement>) => {
  const { t } = useTranslation();

  const gridTemplateColumns = columns
    .map((c) => `minmax(${c.minWidth || 64}px, ${c.maxWidth || '1fr'})`)
    .join(' ');

  return (
    <div className={`Table ${className}`} {...rest}>
      <div className='Table__header d-grid p-2' style={{ gridTemplateColumns }}>
        {columns.map((c) => (
          <div key={c.label} className='Table__cell'>
            {t(c.label)}
          </div>
        ))}
      </div>
      {data.map((row, i) =>
        renderCustomRow ? (
          renderCustomRow(row, columns, gridTemplateColumns, i)
        ) : (
          <div
            className='Table__row d-grid p-2'
            key={row.id || i}
            style={{ gridTemplateColumns }}
          >
            {columns.map((c) => (
              <div key={c.label} className='Table__cell'>
                {c.formatter ? c.formatter(row) : row[c.field]}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};
